import type { AuthService } from '../core/types';
import {
  registerAccountSettingsReader,
  registerAccountSettingsWriter,
  HIGHLIGHT_COLORS
} from '../settings';
import { ensureFirestoreDb, type FirestoreServiceOptions } from './firestore-service';

interface FirestoreDocSnap {
  exists: boolean;
  data(): Record<string, unknown>;
}

interface FirestoreDocRef {
  get(): Promise<FirestoreDocSnap>;
  set(data: Record<string, unknown>, opts?: { merge?: boolean }): Promise<void>;
  onSnapshot(cb: (snap: FirestoreDocSnap) => void, errCb?: (err: unknown) => void): () => void;
}

type SettingsCache = Record<string, Record<string, unknown>>;

function normalizeSettings(raw: unknown): SettingsCache {
  const out: SettingsCache = {};
  if (!raw || typeof raw !== 'object') return out;
  const obj = raw as Record<string, unknown>;
  Object.keys(obj).forEach((domain) => {
    const maybeDomain = obj[domain];
    if (!maybeDomain || typeof maybeDomain !== 'object') return;
    const src = maybeDomain as Record<string, unknown>;
    const next: Record<string, unknown> = {};
    Object.keys(src).forEach((k) => {
      if (src[k] !== undefined) next[k] = src[k];
    });
    out[domain] = next;
  });
  return out;
}

function applyHighlightFromSettings(settings: SettingsCache): void {
  const idRaw =
    settings.appearance?.highlightColorId ??
    settings.tts?.highlightColorId ??
    null;
  if (!idRaw) return;
  const id = String(idRaw);
  const color = HIGHLIGHT_COLORS.find((c) => c.id === id);
  if (!color || typeof document === 'undefined') return;
  document.documentElement.style.setProperty('--notes-tts-highlight-rgb', color.rgb);
  const reveal = document.querySelector('.reveal');
  if (reveal) (reveal as HTMLElement).style.setProperty('--notes-tts-highlight-rgb', color.rgb);
}

export function installFirestoreSettingsSync(
  authService: AuthService,
  opts: FirestoreServiceOptions
): void {
  let cache: SettingsCache | null = null;
  let activeUid = '';
  let unsubDoc: (() => void) | null = null;
  let writeQueue: Promise<void> = Promise.resolve();

  registerAccountSettingsReader(() => cache);

  function clearUserCache(): void {
    cache = null;
    if (unsubDoc) {
      unsubDoc();
      unsubDoc = null;
    }
  }

  function bindUserDoc(uid: string): void {
    ensureFirestoreDb(opts)
      .then((db) => {
        const ref = db.collection('users').doc(String(uid)) as unknown as FirestoreDocRef;
        return ref.get().then((snap) => {
          cache = normalizeSettings(snap.exists ? snap.data().settings : null);
          applyHighlightFromSettings(cache || {});
          if (unsubDoc) unsubDoc();
          unsubDoc = ref.onSnapshot(
            (nextSnap) => {
              cache = normalizeSettings(nextSnap.exists ? nextSnap.data().settings : null);
              applyHighlightFromSettings(cache || {});
            },
            () => {}
          );
        });
      })
      .catch(() => {});
  }

  function syncFromCurrentUser(): void {
    const u = authService.getCurrentUser?.() ?? null;
    const uid = u && !u.isAnonymous && u.uid ? String(u.uid) : '';
    if (!uid) {
      activeUid = '';
      clearUserCache();
      return;
    }
    if (uid === activeUid && cache) return;
    activeUid = uid;
    clearUserCache();
    bindUserDoc(uid);
  }

  authService.subscribeUserChange?.(() => syncFromCurrentUser());
  syncFromCurrentUser();

  registerAccountSettingsWriter((domain, key, value) => {
    const u = authService.getCurrentUser?.() ?? null;
    const uid = u && !u.isAnonymous && u.uid ? String(u.uid) : '';
    if (!uid) return;

    const safeCache = cache || {};
    const safeDomain = { ...(safeCache[domain] || {}) };
    if (value == null || value === '') delete safeDomain[key];
    else safeDomain[key] = value;
    cache = { ...safeCache, [domain]: safeDomain };
    if (key === 'highlightColorId') applyHighlightFromSettings(cache);

    writeQueue = writeQueue
      .then(async () => {
        const db = await ensureFirestoreDb(opts);
        const ref = db.collection('users').doc(String(uid)) as unknown as FirestoreDocRef;
        await ref.set(
          {
            settings: {
              [domain]: {
                [key]: value == null || value === '' ? null : value
              }
            },
            updatedAt: Date.now()
          },
          { merge: true }
        );
      })
      .catch(() => {});
  });
}

